import * as React from "react";
import { Row, Input, Col, Select, Form, FormInstance } from "antd";
import { contactsStore as allUsersContactStore } from "Stores/ContactsStore";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { formatUsername } from "utils/formatUsername";

class Blank extends React.Component {
  render() {
    return null;
  }
}

const RecipientSelect = (props) => {
  return (
    <Select disabled={props.isUserHidden} value={props.value?.id} onChange={props.onChange}>
      {props.recipientsList.map((contact) => (
        <Select.Option key={`${contact.id}`} value={contact.id}>
          {contact.name}
        </Select.Option>
      ))}
    </Select>
  );
};

interface InvoiceRecipientProps extends WithTranslation {
  form: React.RefObject<FormInstance>;
  isAdditional: boolean; // Switcher "Allow commissioning user" in ptintjob settings
  additionalId: number; // ID from "commissioning user" field in ptintjob settings
  defaultId: number; // ID from "default user" field in ptintjob settings
  userId: number;
  isRestricted: boolean;
}

interface InvoiceRecipientState {
  recipients: API_PrintJobRecipient[];
  currentRecipient: API_Contact;
}

export const InvoiceRecipient = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<InvoiceRecipientProps, InvoiceRecipientState> {
      constructor(props: InvoiceRecipientProps) {
        super(props);
        this.state = {
          recipients: this.recipientsList,
          currentRecipient: this.getInitialRecipient(),
        };
      }

      get isUserHidden() {
        const { userId } = this.props;
        return allUsersContactStore.projectUsers.find(({ id }) => id === +userId)?.projectRole === "HID";
      }

      getSpecificUser = (specificUserId: number | string): API_Contact => {
        const user = allUsersContactStore.projectUsers.find(({ id }) => id === +specificUserId) || ({} as API_User);
        const contact: API_Contact = {
          id: user.id,
          name: formatUsername({ firstName: user.firstName, surname: user.surname }),
          company: user.companyName,
          street: user.street,
          postalCode: user.postalCode,
          location: user.city,
        };
        return contact;
      };

      getInitialRecipient = () => {
        const { userId, defaultId, isRestricted } = this.props;
        if (isRestricted) {
          return this.getSpecificUser(userId);
        }

        const standardRecipientId = defaultId !== -1 ? defaultId : userId; // -1 means "Deactivated"
        return this.getSpecificUser(standardRecipientId);
      };

      get recipientsList() {
        const { isAdditional, additionalId, defaultId, isRestricted, userId: currentUserId } = this.props;

        if (isRestricted) {
          return [this.getSpecificUser(currentUserId)];
        }

        if (!isAdditional) {
          return allUsersContactStore.projectUsers
            .filter(({ active }) => active)
            .map(({ id }) => this.getSpecificUser(id));
        } else {
          const userIds = [];
          if (defaultId !== -1) {
            userIds.push(defaultId);
          }
          // basically check if all id's are unique
          if (defaultId !== currentUserId) {
            userIds.push(currentUserId);
          }
          if (
            isAdditional &&
            additionalId &&
            additionalId !== -1 &&
            additionalId !== currentUserId &&
            additionalId !== defaultId
          ) {
            userIds.push(additionalId);
          }

          return userIds.map((user) => this.getSpecificUser(user)).filter((item) => item.id);
        }
      }

      render() {
        const { t } = this.props;
        const { name, company, street, postalCode, location, id } = this.state.currentRecipient;
        return (
          <Row justify="start" align="middle" gutter={24}>
            <Col span={24}>
              <Form.Item
                label={t("Invoice recipient")}
                name="invoiceRecipient"
                initialValue={this.state.currentRecipient}
                getValueFromEvent={(value) => {
                  const currentRecipient = this.getSpecificUser(value);
                  this.setState({ currentRecipient });
                  return currentRecipient;
                }}
              >
                <RecipientSelect isUserHidden={this.isUserHidden} recipientsList={this.recipientsList} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item noStyle initialValue={id} name={["invoiceRecipient", "id"]}>
                <Blank />
              </Form.Item>
              <Form.Item label={t("Contact person")}>
                <Input disabled={true} value={name} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t("Company")}>
                <Input disabled={true} value={company} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t("Street")}>
                <Input disabled={true} value={street} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t("Post code")}>
                <Input disabled={true} value={postalCode} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t("City")}>
                <Input disabled={true} value={location} />
              </Form.Item>
            </Col>
          </Row>
        );
      }
    }
  )
);
